import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import { useEffect, useCallback, useMemo, useRef, useState } from "react";
import {
  Bold,
  BookMarked,
  Italic,
  Link as LinkIcon,
  Link2Off,
  List,
  ListOrdered,
  Quote,
  Heading2,
  Heading3,
  Undo2,
  Redo2,
  Pilcrow,
  Check,
} from "lucide-react";
import { tr } from "@/lib/i18n";

/**
 * TipTap-powered WYSIWYG editor that emits clean HTML.
 *
 * Editorial concerns:
 *   - Preserves paragraph/heading/list/blockquote tags verbatim on
 *     output so the WordPress publisher keeps working unchanged
 *     (article body downstream is a plain HTML string).
 *   - StarterKit is configured WITHOUT `image` — images belong in the
 *     Story Card + featured-image workflow, not the body.
 *   - Paste retains formatting from Google Docs / Word (TipTap uses
 *     ProseMirror's DOM parser which strips MS-specific `<o:p>` /
 *     mso-* junk automatically) so an editor can paste a finished
 *     article and get clean semantic HTML on the other side.
 *   - `Link` opens in a new tab by default so links previewed in the
 *     editor don't yank the editor out of context.
 */
export default function RichTextEditor({
  value,
  onChange,
  placeholder = tr("Paste or type the article body…"),
  minHeight = 320,
  testId = "rich-editor",
  sources = [],   // optional list of { url, outlet, title } — enables
                  //   the "Cite source" toolbar dropdown
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Body HTML shouldn't own its own H1 — the article's headline
        // lives in a separate field. Cap headings to H2/H3.
        heading: { levels: [2, 3] },
        // Keep bullet/ordered lists tight (WordPress themes render
        // them awkwardly with the default loose spacing).
        bulletList: { keepMarks: true, keepAttributes: true },
        orderedList: { keepMarks: true, keepAttributes: true },
      }),
      Link.configure({
        openOnClick: false,        // stay inside the editor on click
        autolink: true,            // paste a URL and it becomes a link
        HTMLAttributes: {
          rel: "noopener noreferrer nofollow",
          target: "_blank",
        },
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        "data-testid": testId,
        // The `.prose-editor` class targets THIS node directly (see
        // index.css — selectors are `.prose-editor h2`, `.prose-editor
        // p`, etc, NOT `.prose-editor .ProseMirror`).
        class:
          "prose-editor px-3 py-3 bg-white outline-none focus:outline-none",
        style: `min-height: ${minHeight}px`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      // TipTap emits `<p></p>` for an empty doc — normalise that to
      // an empty string so the "did the user provide a body?" check
      // upstream doesn't get fooled by boilerplate markup.
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Keep the editor in sync when parent state changes externally
  // (e.g. an "AI SEO subhead" mutation rewrites the body). Only
  // reset when the incoming value actually differs from the editor's
  // current HTML — otherwise every keystroke would blow the cursor
  // back to the start.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (incoming && incoming !== current) {
      editor.commands.setContent(incoming, false);
    } else if (!incoming && current !== "<p></p>") {
      editor.commands.clearContent(false);
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href || "";
    const url = window.prompt(
      tr("Link URL (leave empty to remove):"),
      previous,
    );
    if (url === null) return;              // user cancelled
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run();
  }, [editor]);

  // ------------------------------------------------------------------
  // "Cite source" dropdown — the mobile-friendly way to weave one of
  // the article's known sources into the body as a hyperlink. Rebuilt
  // on every render so the "uncited" list stays honest as links are
  // inserted; expensive strings (like body_html) are only re-scanned
  // when the source list itself changes.
  // ------------------------------------------------------------------
  const [sourceMenuOpen, setSourceMenuOpen] = useState(false);
  const sourceMenuRef = useRef(null);

  // Close on outside click / Esc so mobile taps outside the menu
  // dismiss it cleanly (no phantom overlay hanging around).
  useEffect(() => {
    if (!sourceMenuOpen) return undefined;
    const onDown = (e) => {
      if (!sourceMenuRef.current) return;
      if (!sourceMenuRef.current.contains(e.target)) setSourceMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setSourceMenuOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [sourceMenuOpen]);

  // Scan the current HTML for each source's URL so the menu shows a
  // small ✓ next to sources already linked in the body — same badge
  // logic the read-only SourcesPanel uses, just inline in the picker.
  const sourceCitedStatus = useMemo(() => {
    if (!editor || !sources.length) return {};
    const html = editor.getHTML();
    const status = {};
    for (const s of sources) {
      if (!s.url) continue;
      const escaped = s.url.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const re = new RegExp(
        `<a[^>]*\\bhref\\s*=\\s*(["']?)${escaped}\\1`,
        "i",
      );
      status[s.url] = re.test(html);
    }
    return status;
    // getHTML() isn't reactive on its own — recompute when the parent
    // `value` prop shifts (each keystroke triggers onChange → parent
    // state update → new value prop → this memo re-runs).
  }, [editor, sources, value]);

  const insertSourceLink = useCallback(
    (source) => {
      if (!editor) return;
      const { url, outlet, title } = source;
      if (!url) return;
      const label = (outlet || title || url).trim();
      const { from, to } = editor.state.selection;
      if (from === to) {
        // No selection — drop in a self-contained "See more via …"
        // fragment at the caret. Wrapped in italics to match the
        // existing SourcesPanel injection style.
        editor
          .chain()
          .focus()
          .insertContent([
            { type: "text", text: " " },
            {
              type: "text",
              text: label,
              marks: [{ type: "link", attrs: { href: url } }],
            },
          ])
          .run();
      } else {
        // Wrap the current selection as a link to this source URL —
        // classic "highlight text → cite it" flow, works perfectly
        // with a mobile long-press selection.
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: url })
          .run();
      }
      setSourceMenuOpen(false);
    },
    [editor],
  );

  if (!editor) return null;

  return (
    <div className="border border-[var(--border)] bg-white">
      <div
        className="sticky top-0 z-20 flex flex-wrap items-center gap-0.5 border-b border-[var(--border)] px-1.5 py-1 bg-[var(--secondary)]/95 backdrop-blur-sm"
        data-testid={`${testId}-toolbar`}
      >
        <ToolbarBtn
          onClick={() => editor.chain().focus().setParagraph().run()}
          active={editor.isActive("paragraph")}
          label={tr("Paragraph")}
        >
          <Pilcrow className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          active={editor.isActive("heading", { level: 2 })}
          label={tr("Heading 2")}
        >
          <Heading2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          active={editor.isActive("heading", { level: 3 })}
          label={tr("Heading 3")}
        >
          <Heading3 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarSep />
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          label={tr("Bold")}
        >
          <Bold className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          label={tr("Italic")}
        >
          <Italic className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarSep />
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          label={tr("Bulleted list")}
        >
          <List className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          label={tr("Numbered list")}
        >
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}
          label={tr("Blockquote")}
        >
          <Quote className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarSep />
        <ToolbarBtn
          onClick={setLink}
          active={editor.isActive("link")}
          label={tr("Insert / edit link")}
        >
          <LinkIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        {editor.isActive("link") && (
          <ToolbarBtn
            onClick={() =>
              editor.chain().focus().extendMarkRange("link").unsetLink().run()
            }
            label={tr("Remove link")}
          >
            <Link2Off className="w-3.5 h-3.5" />
          </ToolbarBtn>
        )}
        {sources.length > 0 && (
          <div
            ref={sourceMenuRef}
            className="relative"
            data-testid={`${testId}-source-picker`}
          >
            <ToolbarBtn
              onClick={() => setSourceMenuOpen((v) => !v)}
              active={sourceMenuOpen}
              label={tr("Cite a source")}
            >
              <BookMarked className="w-3.5 h-3.5" />
            </ToolbarBtn>
            {sourceMenuOpen && (
              <div
                data-testid={`${testId}-source-menu`}
                className="absolute left-0 top-full mt-1 z-30 w-[min(88vw,340px)] max-h-72 overflow-y-auto border border-[var(--border)] bg-white shadow-lg"
              >
                <div className="px-3 py-2 border-b border-[var(--border)] text-[10px] uppercase tracking-wider text-[var(--ink-muted)] font-mono-display">{tr("Cite a source")}</div>
                <div className="px-3 py-1.5 text-[11px] text-[var(--ink-muted)] border-b border-[var(--border)]">
                  {editor.state.selection.from === editor.state.selection.to
                    ? tr("No text selected — the source outlet will be linked at the cursor.")
                    : tr("Selected text will become the link.")}
                </div>
                <ul>
                  {sources.map((s, i) => {
                    const cited = !!sourceCitedStatus[s.url];
                    const label = s.outlet || s.title || s.url;
                    return (
                      <li key={`${s.url}-${i}`}>
                        <button
                          type="button"
                          data-testid={`${testId}-source-item-${i}`}
                          onClick={() => insertSourceLink(s)}
                          disabled={cited}
                          className={
                            "w-full text-left px-3 py-2 text-xs border-t border-[var(--border)] first:border-t-0 " +
                            (cited
                              ? "text-[var(--ink-muted)] cursor-not-allowed bg-[var(--secondary)]/50"
                              : "hover:bg-[var(--secondary)]/60")
                          }
                        >
                          <div className="flex items-start gap-2">
                            {cited ? (
                              <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
                            ) : (
                              <BookMarked className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[var(--accent)]" />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-[var(--ink)] truncate">
                                {label}
                              </div>
                              {s.title && s.outlet && s.title !== s.outlet && (
                                <div className="text-[11px] text-[var(--ink-muted)] truncate">
                                  {s.title}
                                </div>
                              )}
                              <div className="text-[10px] text-[var(--ink-muted)] font-mono-display truncate">
                                {s.url}
                              </div>
                              {cited && (
                                <div className="text-[10px] uppercase tracking-wider text-emerald-700 mt-0.5">{tr("Already cited")}</div>
                              )}
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
        <ToolbarSep />
        <ToolbarBtn
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          label={tr("Undo")}
        >
          <Undo2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          label={tr("Redo")}
        >
          <Redo2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
}

function ToolbarBtn({ onClick, active, disabled, label, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      data-testid={`editor-btn-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={
        "inline-flex items-center justify-center w-8 h-8 sm:w-7 sm:h-7 border transition-colors " +
        (active
          ? "bg-[var(--slate-900)] text-white border-[var(--slate-900)]"
          : "bg-white text-[var(--ink)] border-transparent hover:border-[var(--border)]") +
        (disabled ? " opacity-30 cursor-not-allowed" : "")
      }
    >
      {children}
    </button>
  );
}

function ToolbarSep() {
  return <span className="mx-0.5 w-px h-4 bg-[var(--border)]" />;
}
